import { motion, type Variants } from "framer-motion"
import type { AdvancedFeature } from "../data/advanced-features"
import { FLAG_SCROLL_DURATION, FLAG_SEQUENCE } from "../data/flag-sequence"
import { LandingChipComingSoon } from "../LandingChipComingSoon"
import { ScanlinesOverlay } from "./ScanlinesOverlay"

interface AdvancedFeatureCardProps {
  feature: AdvancedFeature
  index: number
  isDesktop?: boolean
  titleStyles: string
  descriptionStyles: string
  cardBorderStyles: string
  cardVariants?: Variants
  onClick?: () => void
  width?: string
}

export function AdvancedFeatureCard({
  feature,
  index,
  isDesktop = false,
  titleStyles,
  descriptionStyles,
  cardBorderStyles,
  cardVariants,
  onClick,
  width,
}: AdvancedFeatureCardProps) {
  const getIconDimensions = () => {
    const dimensions = {
      "AI Edge Insights": "6.7409rem",
      "Autonomous Market Making": "6.4741rem",
      "Live Betting IQ": "6.5824rem",
      "In Feed Alerts": "7.7456rem",
    } as const

    const size = dimensions[feature.title as keyof typeof dimensions] || (index === 0 ? "5.4345rem" : "5.534375rem")
    return { width: size, height: size }
  }

  const getIconTransform = () => {
    if (index === 0) return "translateX(-0.7rem)"
    if (feature.title === "In Feed Alerts") return "translateY(-0.95rem)"
    if (feature.title === "Autonomous Market Making") return "translateY(-0.4rem)"
    if (feature.title === "Live Betting IQ") return "translateY(-0.53rem)"
    if (feature.title === "AI Edge Insights") {
      return isDesktop ? "translateY(-0.93rem) translateX(-0.34rem)" : "translateY(-0.93rem) translateX(-0.34rem)"
    }
    return undefined
  }

  const cardContent = (
    <div
      className={`relative p-6 ${
        isDesktop ? "pb-[.8rem]" : "pb-[1.8rem] sm:pb-[1.2rem] lg:pb-[1.8rem]"
      } rounded-2xl ${cardBorderStyles} ${isDesktop ? "scale-[1.0]" : "scale-[1.03]"} h-full ${
        isDesktop ? "" : "aspect-square sm:h-[280px] sm:aspect-auto lg:h-full lg:aspect-square"
      } flex flex-col`}
      style={{
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        background: "linear-gradient(135deg, rgba(0, 28, 255, 0.04) 0%, rgba(0, 28, 255, 0.02) 25%, transparent 50%)",
        boxShadow: "inset 0 0 20px rgba(255, 255, 255, 0.1)",
        ...(isDesktop && index === 4 ? { minHeight: "180px" } : {}),
      }}
    >
      <ScanlinesOverlay />
      <div className={`flex-1 flex flex-col ${isDesktop ? "" : "relative"}`}>
        {isDesktop && feature.iconUrl && (
          <div className="relative mb-4">
            <img
              src={feature.iconUrl}
              alt={feature.title}
              className="relative z-10 select-none"
              style={{
                ...getIconDimensions(),
                objectFit: "contain",
                transform: getIconTransform(),
              }}
            />
          </div>
        )}

        {!isDesktop && feature.iconUrl && (
          <div className="absolute top-0 left-0">
            <img
              src={feature.iconUrl}
              alt={feature.title}
              className="relative z-10 select-none"
              style={{
                ...getIconDimensions(),
                objectFit: "contain",
                transform: getIconTransform(),
              }}
            />
          </div>
        )}

        {feature.title.toLowerCase() === "global fan network" &&
          (isDesktop ? (
            <div
              className="absolute pointer-events-none overflow-hidden z-101"
              style={{
                left: "5.1em",
                right: 0,
                top: "4.4rem",
                height: "2.2rem",
                zIndex: 101,
                maskImage: "linear-gradient(to right, transparent 0%, black 7.5%, black 85%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 7.5%, black 85%, transparent 100%)",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  whiteSpace: "nowrap",
                  animation: `flag-scroll ${FLAG_SCROLL_DURATION}s linear infinite`,
                }}
              >
                {[...FLAG_SEQUENCE, ...FLAG_SEQUENCE].map((flag, idx) => {
                  const originalLength = FLAG_SEQUENCE.length
                  const isLastInSequence = (idx + 1) % originalLength === 0
                  return (
                    <span
                      // biome-ignore lint/suspicious/noArrayIndexKey: Flags repeat in sequence - index needed to distinguish duplicates
                      key={`flag-${flag}-${idx}`}
                      style={{
                        marginRight: isLastInSequence ? 0 : "0.25rem",
                        fontSize: "1.11rem",
                        lineHeight: 1,
                        animation: "flag-wave 2s ease-in-out infinite",
                        animationDelay: `${idx * 0.15}s`,
                        display: "inline-block",
                      }}
                    >
                      {flag}
                    </span>
                  )
                })}
              </div>
            </div>
          ) : (
            <div
              className="absolute pointer-events-none overflow-hidden z-101"
              style={{
                left: "3.6em",
                right: "-1.0rem",
                top: "2.8rem",
                height: "2.2rem",
                zIndex: 101,
                maskImage: "linear-gradient(to right, transparent 0%, black 7.5%, black 85%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 7.5%, black 85%, transparent 100%)",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  whiteSpace: "nowrap",
                  animation: `flag-scroll ${FLAG_SCROLL_DURATION}s linear infinite`,
                }}
              >
                {[...FLAG_SEQUENCE, ...FLAG_SEQUENCE].map((flag, idx) => {
                  const originalLength = FLAG_SEQUENCE.length
                  const isLastInSequence = (idx + 1) % originalLength === 0
                  return (
                    <span
                      // biome-ignore lint/suspicious/noArrayIndexKey: Flags repeat in sequence - index needed to distinguish duplicates
                      key={`flag-${flag}-${idx}`}
                      style={{
                        marginRight: isLastInSequence ? 0 : "0.25rem",
                        fontSize: "1.11rem",
                        lineHeight: 1,
                        animation: "flag-wave 2s ease-in-out infinite",
                        animationDelay: `${idx * 0.15}s`,
                        display: "inline-block",
                      }}
                    >
                      {flag}
                    </span>
                  )
                })}
              </div>
            </div>
          ))}

        {feature.title === "In Feed Alerts" && (
          <LandingChipComingSoon
            label="UP NEXT 📈"
            className={
              isDesktop
                ? "absolute lg:top-8 lg:right-8 xl:top-8 xl:right-8 2xl:top-8 2xl:right-8 z-102 rotate-[-4deg]"
                : "absolute top-0 right-[-.05rem] sm:right-[.15rem] md:right-[.4rem] md:top-[.25rem] z-102 rotate-[-4deg]"
            }
          />
        )}

        <div
          className={isDesktop ? "flex flex-col gap-1 mt-auto" : "absolute bottom-0 left-0 right-0 flex flex-col gap-1"}
        >
          <div className={isDesktop ? "relative mb-[0.15rem]" : "relative mb-[0.05rem]"}>
            <h3 className={titleStyles}>
              {feature.title === "Autonomous Market Making" ? (
                <>
                  <span className="sm:hidden lg:inline">Autonomous Market Making</span>
                  <span className="hidden sm:inline lg:hidden">Auto Market Making</span>
                </>
              ) : (
                feature.title
              )}
            </h3>
          </div>
          <p className={descriptionStyles}>
            {isDesktop ? feature.description : (feature.descriptionMobile ?? feature.description)}
          </p>
        </div>
      </div>

      <div className="absolute inset-0 rounded-2xl bg-linear-to-r from-[#001CFF]/0 via-[#001CFF]/10 to-[#001CFF]/0 opacity-100 z-0" />
    </div>
  )

  if (!isDesktop) {
    return (
      <motion.div
        className="relative flex-none snap-center cursor-pointer"
        data-adv-card="true"
        style={{ width }}
        {...(cardVariants ? { variants: cardVariants } : {})}
        onClick={onClick}
      >
        {cardContent}
      </motion.div>
    )
  }

  return (
    <motion.div
      className="relative"
      {...(cardVariants ? { variants: cardVariants } : {})}
      {...(width ? { style: { width } } : {})}
    >
      {cardContent}
    </motion.div>
  )
}
